import { useInfiniteQuery } from "@tanstack/react-query";
import type { ExtensionCatalogueItem } from "@zworks/console-contracts";
import { ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { PageHeader } from "@/components/bespoke/page-header";
import { EmptyState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { extensionCatalogueQuery } from "@/lib/api";
import { CatalogueError } from "./marketplace";
import { kindLabels, packStatusBadges, stateBadges } from "./marketplace-labels";

/**
 * The pack detail (MK9). The hub reports no description, evaluation, publisher, or signature, so
 * the page shows only what the catalogue carries: kind, version, state, digest, capabilities, and
 * the status and domain the catalogue declared for an imported pack.
 */
export function PackDetailPage() {
  const { packId = "" } = useParams();
  const catalogue = useInfiniteQuery(extensionCatalogueQuery());

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/marketplace"
        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft aria-hidden className="size-4" />
        Marketplace
      </Link>
      {catalogue.isPending ? (
        <LoadingState label="Loading the pack" />
      ) : catalogue.isError && !catalogue.data ? (
        <CatalogueError error={catalogue.error} onRetry={() => void catalogue.refetch()} />
      ) : catalogue.data ? (
        <PackDetail
          item={catalogue.data.pages
            .flatMap((page) => page.items)
            .find((entry) => entry.id === packId)}
          catalogue={catalogue.data.pages.flatMap((page) => page.items)}
          hasMore={catalogue.hasNextPage}
          isLoadingMore={catalogue.isFetchingNextPage}
          loadMoreError={catalogue.isFetchNextPageError}
          onLoadMore={() => void catalogue.fetchNextPage()}
        />
      ) : (
        <LoadingState label="Loading the pack" />
      )}
    </div>
  );
}

function PackDetail({
  item,
  catalogue,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
}: {
  item: ExtensionCatalogueItem | undefined;
  catalogue: ExtensionCatalogueItem[];
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreError: boolean;
  onLoadMore: () => void;
}) {
  if (!item) {
    return (
      <div className="flex flex-col items-start gap-2">
        <EmptyState
          title={
            hasMore
              ? "This pack is not on the loaded pages yet"
              : "This pack is not in the catalogue"
          }
          description={
            hasMore
              ? "Load another page to continue searching the catalogue."
              : "It may have been removed from the catalogue."
          }
        />
        {hasMore ? (
          <CataloguePageButton
            isLoadingMore={isLoadingMore}
            loadMoreError={loadMoreError}
            onLoadMore={onLoadMore}
            label="Load next catalogue page"
          />
        ) : null}
      </div>
    );
  }

  const state = stateBadges[item.state];
  const declared = item.packStatus ? packStatusBadges[item.packStatus] : null;
  const versions = catalogue.filter(
    (entry) => entry.kind === item.kind && entry.name === item.name,
  );
  const capabilities = item.capabilities;
  const loadedPagesNote = "Showing versions from loaded pages";

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={item.name}
        badge={
          <>
            <Badge variant="muted">{kindLabels[item.kind]}</Badge>
            {item.domain ? <Badge variant="muted">{item.domain}</Badge> : null}
            <span className="font-mono text-sm text-muted-foreground">v{item.version}</span>
          </>
        }
        actions={
          <>
            {declared ? <Badge variant={declared.variant}>{declared.label}</Badge> : null}
            <Badge variant={state.variant}>{state.label}</Badge>
          </>
        }
      />

      <Tabs defaultValue="overview" className="flex flex-col gap-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
          <TabsTrigger value="versions">Versions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardContent>
              <dl className="grid gap-4 sm:grid-cols-2">
                <Fact label="Pinned to">
                  <code className="break-all font-mono text-xs">{item.digest}</code>
                </Fact>
                <Fact label="Capabilities">
                  {capabilities.length} {capabilities.length === 1 ? "capability" : "capabilities"}
                </Fact>
                <Fact label="Versions">
                  {versions.length}
                  {hasMore ? (
                    <span className="block text-xs text-muted-foreground">{loadedPagesNote}</span>
                  ) : null}
                </Fact>
              </dl>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="capabilities">
          <Card>
            <CardContent>
              {capabilities.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  This version declares no capabilities.
                </p>
              ) : (
                <ul role="list" className="flex flex-col gap-2">
                  {capabilities.map((capability) => (
                    <li key={capability} className="font-mono text-sm text-foreground">
                      {capability}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="versions">
          <Card>
            <CardContent className="flex flex-col gap-4">
              {hasMore ? <p className="text-sm text-muted-foreground">{loadedPagesNote}</p> : null}
              <ul role="list" className="flex flex-col divide-y divide-border">
                {versions.map((version) => {
                  const badge = stateBadges[version.state];
                  return (
                    <li
                      key={version.id}
                      className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm">v{version.version}</span>
                        {version.id === item.id ? (
                          <Badge variant="muted">This version</Badge>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <code className="text-xs text-muted-foreground">
                          {version.digest.slice(0, 19)}…
                        </code>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      {hasMore ? (
        <CataloguePageButton
          isLoadingMore={isLoadingMore}
          loadMoreError={loadMoreError}
          onLoadMore={onLoadMore}
          label="Load more catalogue results"
        />
      ) : null}
    </div>
  );
}

function CataloguePageButton({
  isLoadingMore,
  loadMoreError,
  onLoadMore,
  label,
}: {
  isLoadingMore: boolean;
  loadMoreError: boolean;
  onLoadMore: () => void;
  label: string;
}) {
  return (
    <div className="flex flex-col items-start gap-2">
      {loadMoreError ? (
        <p role="alert" className="text-sm text-destructive-text">
          More catalogue results could not be loaded. Try again.
        </p>
      ) : null}
      <Button variant="outline" onClick={onLoadMore} disabled={isLoadingMore}>
        {isLoadingMore ? "Loading more results…" : loadMoreError ? "Try again" : label}
      </Button>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}
